import { type FC, useEffect, useRef, useState } from 'react';

import type { ComposerGif } from '../../CommunityPostComposer.type';
import { DEFAULT_GIF_SEARCH_DEBOUNCE_MS, DEFAULT_GIF_SEARCH_LIMIT } from '../constants';

interface GifPickerLabels {
  searchPlaceholder: string;
  loading: string;
  error: string;
  noResults: string;
  poweredBy: string;
}

interface GifPickerProps {
  onSearchGifs: (query: string, opts: { limit: number; signal: AbortSignal }) => Promise<ComposerGif[]>;
  onSelect: (gif: ComposerGif) => void;
  labels: GifPickerLabels;
  gifSearchLimit?: number;
  gifSearchDebounceMs?: number;
  autoFocus?: boolean;
}

export const GifPicker: FC<GifPickerProps> = ({
  onSearchGifs,
  onSelect,
  labels,
  gifSearchLimit = DEFAULT_GIF_SEARCH_LIMIT,
  gifSearchDebounceMs = DEFAULT_GIF_SEARCH_DEBOUNCE_MS,
  autoFocus = true,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ComposerGif[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(false);
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    const trimmed = query.trim();
    const debounceMs = trimmed ? gifSearchDebounceMs : 0;
    const handle = setTimeout(() => {
      onSearchGifs(trimmed, { limit: gifSearchLimit, signal: controller.signal })
        .then((next) => {
          if (controller.signal.aborted) return;
          setResults(next);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          if (controller.signal.aborted) return;
          setResults([]);
          setError(true);
          setLoading(false);
        });
    }, debounceMs);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [query, onSearchGifs, gifSearchLimit, gifSearchDebounceMs]);

  return (
    <>
      <div className="p-2 border-b border-black/5">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={labels.searchPlaceholder}
          aria-label={labels.searchPlaceholder}
          // biome-ignore lint/a11y/noAutofocus: focus is expected when popover opens
          autoFocus={autoFocus}
          className="w-full px-3 py-1.5 text-sm rounded-full bg-black/5 outline-none focus:ring-2 focus:ring-black/20"
        />
      </div>
      <div className="max-h-72 overflow-y-auto p-2">
        {loading && <p className="text-sm text-black/50 text-center py-4">{labels.loading}</p>}
        {!loading && error && <p className="text-sm text-red-500 text-center py-4">{labels.error}</p>}
        {!loading && !error && results.length === 0 && (
          <p className="text-sm text-black/50 text-center py-4">{labels.noResults}</p>
        )}
        {!loading && !error && results.length > 0 && (
          <div className="grid grid-cols-2 gap-1">
            {results.map((gif) => {
              const thumb = gif.thumbUrl ?? gif.url;
              return (
                <button
                  key={gif.id}
                  type="button"
                  onClick={() => onSelect(gif)}
                  aria-label={gif.title || 'GIF'}
                  className="block w-full overflow-hidden rounded-md bg-black/5 hover:ring-2 hover:ring-black/20 focus:outline-none focus:ring-2 focus:ring-black/20"
                >
                  <img
                    src={thumb}
                    alt={gif.title ?? ''}
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    className="block w-full h-auto"
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
      <p className="px-3 py-2 text-[11px] leading-4 text-black/40 border-t border-black/5">{labels.poweredBy}</p>
    </>
  );
};
